import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient
} from 'react-query';
import {
  positionOpenerAxiosApi,
  openerOrchestratorAxiosApi
} from 'src/Config/axiosApi';
import { resolveOpenerApi } from './openerEndpoint';
import {
  GroupSelection,
  ALL_GROUPS,
  groupSelectionKey
} from '../components/entry/groupFilter';
import type { HedgeMode } from '../components/shared/counterHedgeMode';
import {
  getOpenerState,
  startOpener,
  stopOpener,
  clearOpenerPanicLockout,
  OpenerState
} from 'src/Api/exchanges';
import {
  EntryRule,
  EntryRuleType,
  EntryExecution,
  CreateEntryRuleDto,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  EntryOrdersResponse,
  MakerFlag
} from '../types/entry.types';
import {
  recoverFromDuplicateCreate,
  seedRuleIntoLists,
  mergeRuleDtoOptimistic,
  rollbackRuleInList
} from './ruleCreation';
import { enqueueOpenerWrite } from './requestQueue';
import {
  ensureOrchestratorBooted,
  useBootOrchestrator,
  useShutdownOrchestrator
} from './useOrchestratorBoot';

/**
 * Status set the opener page operates on. `expired` is included because
 * prediction-market expiry dates are frequently wrong (an intraday market
 * stored hours early flips to `expired` while still live); the operator must
 * still see and send such a pair to the opener. The bucket page's
 * `splitActiveExpired` routes the expired ones into the Expired bucket.
 */
export const OPENER_PAIR_STATUS = 'active,expired';

/**
 * Debounced reconcile for the `entry-rules` list. Each maker-flag toggle
 * optimistically patches the row switch, then used to fire its OWN full-list
 * refetch on settle. Flipping several legs fast → N refetches racing; a stale
 * in-flight one lands on top of a later optimistic patch and a just-enabled
 * switch snaps back / looks frozen (the symptom behind the bursty-enable
 * lag + errors). Collapsing the post-toggle reconcile into ONE trailing
 * refetch keeps the optimistic UI authoritative through the burst; the slow
 * backstop refetch catches anything the trailing refetch misses. Shared at
 * module scope so a whole burst of toggles coalesces into a single refetch.
 */
export const ENTRY_RULES_RECONCILE_DEBOUNCE_MS = 600;
let entryRulesReconcileTimer: ReturnType<typeof setTimeout> | null = null;
export function debouncedInvalidateEntryRules(qc: QueryClient): void {
  if (entryRulesReconcileTimer) clearTimeout(entryRulesReconcileTimer);
  entryRulesReconcileTimer = setTimeout(() => {
    entryRulesReconcileTimer = null;
    void qc.invalidateQueries('entry-rules');
  }, ENTRY_RULES_RECONCILE_DEBOUNCE_MS);
}

/**
 * Resolve the opener axios instance a per-rule WRITE should target, given the
 * rule's `_id`. A grouped rule must activate/pause/toggle/maker-flag against
 * its owning group's feed pod (core 409s grouped writes); ungrouped pairs use
 * the core base.
 *
 * The group is read STRAIGHT OFF the rule object in the `entry-rules` cache
 * (`rule.openerGroupId`) — the page's authoritative, always-loaded source.
 * It deliberately NO LONGER cross-joins the volatile `manual-confirmed-pairs`
 * cache: a miss there (in-flight refetch, a pair added to a group after the
 * cache loaded) used to resolve `null` and silently route a grouped write to
 * the CORE pod, which served it 200 and re-armed the rule (spec D4/CR-4).
 *
 * Routing is fail-visible: a grouped rule whose feed base can't be resolved
 * THROWS (`UnresolvedOpenerGroupError`) instead of falling back to core — the
 * caller runs this inside its `mutationFn`, so the throw reaches the mutation
 * `onError` + the app-wide `MutationCache.onError` toast. A rule absent from
 * the cache (`openerGroupId` undefined) is treated as ungrouped → core; the
 * page binds rows to the rule cache, so a write is only issued for a rule the
 * cache holds. The returned instance carries the shared bearer/refresh
 * interceptors (see `axiosApi.ts`).
 *
 * The scan takes the first GROUPED copy and only falls back to core when NO
 * cache holds a group for the rule. It deliberately does not stop at the first
 * cache that merely CONTAINS the rule: `getQueriesData` returns the caches in
 * no meaningful order, and a copy fetched BEFORE the pair was assigned to its
 * group (routine for a manually-assigned pair) carries no `openerGroupId` —
 * stopping there routed the write to core, the same D4/CR-4 failure by another
 * door (observed 2026-08-07: every write on a grouped rule landed on core).
 * Preferring the grouped copy also fails in the SAFE direction if the stale
 * copy is the grouped one: the write reaches a feed pod that answers 404
 * rather than silently bypassing the single-writer invariant on core.
 */
export function resolveOpenerApiForRule(qc: QueryClient, ruleId: string) {
  let openerGroupId: string | null = null;
  for (const [, rules] of qc.getQueriesData<EntryRule[]>('entry-rules')) {
    const groupId = rules?.find(r => r._id === ruleId)?.openerGroupId;
    if (groupId) {
      openerGroupId = groupId;
      break;
    }
  }
  return resolveOpenerApi(openerGroupId);
}

/**
 * Fetch entry rules for the active group selection, optionally filtered by
 * `ruleType` ('tna' or 'sum_of_sides'). Mirrors `useUnwindingRules` so the
 * page-level slicing patterns (active vs paused, by pairId) carry over
 * unchanged.
 *
 * Scoping mirrors `resolveEntrySocketTarget` (see `useEntrySocket`):
 * - a CONCRETE group reads its OWN feed pod, group-prefixed, and passes
 *   `groupId` so the backend returns only that group's rules PLUS the legacy
 *   null-group bucket — never the full ~9.5k-doc collection;
 * - `'ungrouped'` stays on the CORE base but passes `groupId: ''` — the null
 *   bucket ONLY (~847 paused rules), the same `'' = ungrouped` convention
 *   `/bootstrap` uses. An ABSENT param is what triggers the unfiltered ~9.5k
 *   query, so ungrouped must send the empty string, never omit it.
 * - `'all'` does NOT fire the query at all (`enabled: false`) — a plain visit
 *   to the opener page would otherwise fire the unfiltered query that starves
 *   the core pod. The page renders a group-picker landing instead of the
 *   table, so no row ever binds to an absent rule.
 *
 * A concrete group whose feed base can't be resolved fails VISIBLY
 * (`resolveOpenerApi` throws `UnresolvedOpenerGroupError`) rather than falling
 * back to core's poisoned unfiltered list — the same fail-closed rule the
 * write path uses (see `openerEndpoint.ts`).
 *
 * The query key carries the selection so switching groups never serves another
 * group's rules from cache. The live per-row on/off state does NOT come from
 * this query (that rides the `opening_state*` WS envelope) — this only carries
 * rule CONFIG, and every mutation invalidates it — so there is no fast poll;
 * the 60s interval is a slow backstop.
 */
export function useEntryRules(opts?: {
  ruleType?: EntryRuleType;
  groupSelection?: GroupSelection;
}) {
  const ruleType = opts?.ruleType;
  const selection = opts?.groupSelection ?? ALL_GROUPS;
  // A concrete group → its own id (→ feed pod); `'ungrouped'` → `''` (core, null
  // bucket only); `'all'` → `null` (query never runs). `null` distinguishes
  // "don't send a param" (all) from "send the empty-string param" (ungrouped).
  const groupId =
    selection.kind === 'group'
      ? selection.groupId
      : selection.kind === 'ungrouped'
        ? ''
        : null;
  return useQuery<EntryRule[]>(
    ['entry-rules', ruleType ?? 'all', groupSelectionKey(selection)],
    async () => {
      const params: Record<string, string> = {};
      if (ruleType) params.ruleType = ruleType;
      if (groupId !== null) params.groupId = groupId;
      const { data } = await resolveOpenerApi(groupId).get<EntryRule[]>(
        '/rules',
        Object.keys(params).length ? { params } : undefined
      );
      return data;
    },
    {
      enabled: selection.kind !== 'all',
      refetchInterval: 60000,
      keepPreviousData: true
    }
  );
}

export function useCreateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateEntryRuleDto) => {
      try {
        const { data } = await positionOpenerAxiosApi.post<EntryRule>(
          '/rules',
          dto
        );
        return data;
      } catch (err) {
        // Duplicate-create conflict (409 + existingRuleId) means the pair
        // already has a rule — resolve to it so the caller proceeds with
        // its PATCH instead of surfacing a failure toast. Anything else
        // rethrows untouched.
        return recoverFromDuplicateCreate(err, async id => {
          const { data } = await positionOpenerAxiosApi.get<EntryRule>(
            `/rules/${id}`
          );
          return data;
        });
      }
    },
    {
      mutationKey: 'entry:create-rule',
      onSuccess: created => {
        // Seed the rule into every cached `entry-rules` list so rows bind
        // to it the moment the create resolves. Invalidation alone left a
        // ~1-5s window where the row still saw `rule === undefined` and a
        // switch click fired a second create for the same pair.
        seedRuleIntoLists(
          qc.getQueriesData<EntryRule[]>('entry-rules'),
          created
        ).forEach(([key, data]) => qc.setQueryData(key, data));
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Single-rule param update. Routed by `resolveOpenerApiForRule` exactly like
 * `useSetRuleArmed` (grouped → owning feed pod, fail-visible if unresolvable;
 * ungrouped → core): with the production single-writer guard on, core 409s a
 * grouped write, so the per-row confirm (✓) must target the feed pod for the
 * params to actually land on the running rule.
 *
 * Optimistic + reconcile, identical model to `useSetMakerFlag`. Without it the
 * PATCH lands (200) but the row's Mode/Hedge/Wait/Gate/Sweep/Walk drafts snap
 * back: a stale `entry-rules` poll already in flight when the ✓ is pressed
 * lands AFTER the write and reverts the committed field in the cache, so the
 * keyed re-sync effect resets the operator's just-saved dropdown to the old
 * value. `cancelQueries` drops those in-flight polls, the optimistic merge (and
 * the authoritative server rule on success) hold the committed value, and the
 * debounced trailing refetch coalesces a burst of ✓ confirms into ONE reconcile
 * instead of N racing ones.
 */
export function useUpdateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateEntryRuleDto }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).patch<EntryRule>(`/rules/${id}`, dto)
      );
      return data;
    },
    {
      mutationKey: 'entry:update-rule-params',
      onMutate: async ({ id, dto }) => {
        await qc.cancelQueries('entry-rules');
        const snapshots = qc.getQueriesData<EntryRule[]>('entry-rules');
        qc.setQueriesData<EntryRule[] | undefined>('entry-rules', old =>
          old?.map(r => (r._id === id ? mergeRuleDtoOptimistic(r, dto) : r))
        );
        return { snapshots };
      },
      onError: (_err, vars, ctx) => {
        // Roll back ONLY the failed rule (not the whole list snapshot) so a
        // sibling row committed by a concurrent confirm isn't wiped.
        ctx?.snapshots?.forEach(([key, snapshot]) =>
          qc.setQueryData<EntryRule[] | undefined>(key, cur =>
            rollbackRuleInList(cur, snapshot, vars.id)
          )
        );
      },
      onSuccess: updated => {
        // Replace the optimistic merge with server truth (resolved config
        // sides, any backend-derived fields) the moment the write returns.
        if (updated?._id) {
          qc.setQueriesData<EntryRule[] | undefined>('entry-rules', old =>
            old?.map(r => (r._id === updated._id ? updated : r))
          );
        }
      },
      onSettled: () => {
        debouncedInvalidateEntryRules(qc);
      }
    }
  );
}

/**
 * Bulk parameter update across EXISTING entry rules in one request.
 * `scope: 'all'` updates every rule for the given `path`; `scope: 'all'` +
 * `groupId` narrows that to one opener group; `scope: 'all'` + `subgroupId`
 * narrows to one opener subgroup (groupId XOR subgroupId, never both);
 * `scope: 'ids'` updates just the supplied `ruleIds`. Only the filled params
 * (`thresholdSum` / `size` / `maxVolumeContracts` / `makerSizeMode` /
 * `counterStuckMarketHedgeSeconds` / `counterHedgeMode`) are
 * changed — an absent field is left untouched server-side (an explicit
 * `counterHedgeMode: null` clears the override back to the global default). Unlike the per-rule
 * `useUpdateEntryRule` (which routes each write to its owning feed pod), this
 * stays on the CORE base: `/rules/bulk-params` fans grouped writes out
 * server-side with internal origin stamping. Rule-less rows are staged
 * client-side by the caller, not created here.
 */
export function useBulkUpdateEntryRuleParams() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      subgroupId?: string;
      path: 'a' | 'b';
      thresholdSum?: number;
      size?: number;
      maxVolumeContracts?: number;
      makerSizeMode?: 'dynamic' | 'fixed';
      counterStuckMarketHedgeSeconds?: number;
      disablePlacementGatePolymarket?: boolean;
      disablePlacementGateKalshi?: boolean;
      counterMakerWalkStepSeconds?: number | null;
      counterMakerWalkStepCents?: number | null;
      lockstepMaxAheadBatches?: number | null;
      counterHedgeMode?: HedgeMode | null;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: string;
        matched: number;
        modified: number;
      }>('/rules/bulk-params', dto);
      return data;
    },
    {
      mutationKey: 'entry:bulk-params',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/** A single rule that diverged from the representative sample at save time. */
/** POST /rules/groups/:groupId/default-config result. */
export interface SaveGroupDefaultConfigResult {
  saved: true;
  ruleCount: number;
  savedAt: string;
}

/** POST /rules/groups/:groupId/default-config/reset result. */
export interface ResetGroupDefaultConfigResult {
  matched: number;
  modified: number;
  skipped: number;
}

/**
 * Snapshot each member rule's current config as its own per-rule "default" —
 * every rule remembers its OWN params (config-only; never the enable/maker
 * toggles). Re-reads the rules AND the groups list so the freshly stamped
 * `defaultConfigSavedAt` shows up.
 */
export function useSaveGroupDefaultConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } =
        await positionOpenerAxiosApi.post<SaveGroupDefaultConfigResult>(
          `/rules/groups/${groupId}/default-config`
        );
      return data;
    },
    {
      mutationKey: 'entry:save-group-default',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
        qc.invalidateQueries(['opener-groups']);
      }
    }
  );
}

/**
 * Re-apply each member rule's OWN saved default config back onto it — config
 * fields only, NEVER the enable/maker toggles. Applies hot (active rules keep
 * running with the restored params). Rules created after the last Save have no
 * snapshot and are skipped. 400s when no rule in the group has a saved default
 * (the caller gates the button on `defaultConfigSavedAt`). Re-reads the rules
 * since the stamp may have changed many of them.
 */
export function useResetGroupDefaultConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } =
        await positionOpenerAxiosApi.post<ResetGroupDefaultConfigResult>(
          `/rules/groups/${groupId}/default-config/reset`
        );
      return data;
    },
    {
      mutationKey: 'entry:reset-group-default',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useDeleteEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionOpenerAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      platform,
      enabled
    }: {
      id: string;
      platform: 'kalshi' | 'polymarket';
      enabled: boolean;
    }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post(`/rules/${id}/toggle-platform`, {
          platform,
          enabled
        })
      );
      return data;
    },
    {
      mutationKey: 'entry:toggle-platform',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Set one of the four per-(platform, side) maker flags on a rule.
 * Backend's RulesService.update() re-resolves the rule's path and
 * derives kalshiConfig.side / polymarketConfig.side from the merged
 * flag state — the caller only needs to pass the flag it wants to
 * change. The `clearOthers` option lets the caller atomically clear
 * the cross-path flags when switching from path A → path B (avoids
 * a transient cross-path BadRequest from the validator).
 */
export function useSetMakerFlag() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      flag,
      value,
      clearOthers
    }: {
      id: string;
      flag: MakerFlag;
      value: boolean;
      clearOthers?: MakerFlag[];
    }) => {
      const dto: Partial<Record<MakerFlag, boolean>> = { [flag]: value };
      if (clearOthers) {
        for (const f of clearOthers) dto[f] = false;
      }
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).patch<EntryRule>(`/rules/${id}`, dto)
      );
      return data;
    },
    {
      mutationKey: 'entry:set-maker-flag',
      // Optimistically patch the maker flag(s) into every cached
      // `entry-rules` list so the row's switch reflects the click
      // immediately — the switch's `checked` binds to `rule[flag]`, which
      // otherwise only changed after the next refetch landed, making the
      // toggle look frozen / "not taken" after turning a rule off.
      onMutate: async ({ id, flag, value, clearOthers }) => {
        await qc.cancelQueries('entry-rules');
        const snapshots = qc.getQueriesData<EntryRule[]>('entry-rules');
        const patch: Partial<Record<MakerFlag, boolean>> = { [flag]: value };
        if (clearOthers) for (const f of clearOthers) patch[f] = false;
        qc.setQueriesData<EntryRule[] | undefined>('entry-rules', old =>
          old?.map(r => (r._id === id ? { ...r, ...patch } : r))
        );
        return { snapshots };
      },
      onError: (_err, _vars, ctx) => {
        // Roll the optimistic patch back to the pre-mutation snapshots — but
        // only into lists that STILL EXIST. `setQueryData` BUILDS the query
        // when it is missing, so an unguarded restore resurrects a list that a
        // group reassignment dropped (`dropStaleEntryRuleGroups`) while this
        // write was in flight, repopulated with the PRE-MOVE `openerGroupId`.
        // Subsequent writes on that rule would then route at the old group.
        // The check has to skip the CALL — an updater returning `undefined`
        // still leaves a rebuilt, data-less query behind.
        ctx?.snapshots?.forEach(([key, data]) => {
          if (qc.getQueryState(key) === undefined) return;
          qc.setQueryData(key, data);
        });
      },
      onSettled: () => {
        // Reconcile against server truth — picks up backend side-effects
        // the optimistic patch can't know (e.g. status→paused when the
        // last maker flag goes off, resolved config sides). Debounced so a
        // burst of fast leg toggles collapses into ONE trailing refetch
        // instead of N racing ones that clobber the optimistic switches.
        debouncedInvalidateEntryRules(qc);
      }
    }
  );
}

/**
 * Bulk set one of the four maker flags across many entry rules in one
 * round trip. `scope: 'all'` + `groupId` flips every rule in that opener
 * group; `scope: 'all'` + `subgroupId` flips every rule in that opener
 * subgroup (groupId XOR subgroupId, never both); a bare `scope: 'all'`
 * flips every rule; `scope: 'ids'` flips the given `ruleIds`. Same backend
 * cascade as `useSetMakerFlag` per rule —
 * `value: true` ACTIVATES paused rules; the last-flag-off auto-pauses.
 * Mirrors `useSetMakerFlag` / `useBulkSetPairOpenerEnabled`: same
 * `positionOpenerAxiosApi`, same `entry-rules` invalidation, and the global
 * `MutationCache.onError` backstop surfaces failures via the mutationKey.
 */
export function useBulkSetMakerFlag() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      subgroupId?: string;
      flag: MakerFlag;
      value: boolean;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: 'all' | 'ids';
        matched: number;
        modified: number;
        skipped: number;
      }>('/rules/bulk-maker-flag', dto);
      return data;
    },
    {
      mutationKey: 'entry:bulk-set-maker-flag',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Bulk arm/disarm — sets `desiredArmed` across many entry rules in one round
 * trip. `desiredArmed` is now the ONLY arm/disarm trigger (a maker-flag flip no
 * longer arms), so this is the bulk equivalent of the per-row on/off switch.
 * `scope: 'all'` + `groupId` arms/disarms every rule in that opener group;
 * `scope: 'all'` + `subgroupId` every rule in that opener subgroup (groupId XOR
 * subgroupId, never both); a bare `scope: 'all'` every rule; `scope: 'ids'` the
 * given `ruleIds`.
 *
 * Unlike the per-rule `useSetRuleArmed` (which routes each write to its owning
 * feed pod), this stays on the CORE base exactly like `useBulkSetMakerFlag`: the
 * backend `/rules/bulk-armed` endpoint itself fans grouped arming out to the
 * right feed pods, so the FE never does per-group routing for the bulk path.
 * Tagged `entry:bulk-armed` so the app-wide `MutationCache.onError` backstop
 * surfaces a failure with tailored copy.
 */
export function useBulkSetArmed() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      subgroupId?: string;
      armed: boolean;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: 'all' | 'ids';
        matched: number;
        modified: number;
        skipped: number;
      }>('/rules/bulk-armed', dto);
      return data;
    },
    {
      mutationKey: 'entry:bulk-armed',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Hide a pair from the opener page. The pair stays
 * `matchedBy: 'manual'` and visible everywhere else (Opportunities,
 * Positions, Unwinding); only the opener filters it out of
 * `findManualConfirmed`. The PairsSyncService change-stream tick on
 * the backend then drops the Kalshi + Polymarket WS subscription via
 * the existing diff path. Served by the opener-orchestrator, which lives
 * whenever the opener domain lives — the operator must be able to hide a pair
 * with opportunities-orchestrator at 0 replicas.
 * Optimistically removes the pair from the
 * cached `manual-confirmed-pairs` list so the row disappears the
 * moment the operator confirms — server invalidation lands the
 * canonical state right after.
 */
export function useHidePairFromOpener() {
  const qc = useQueryClient();
  return useMutation(
    async (pairId: string) => {
      const { data } = await openerOrchestratorAxiosApi.patch(
        `/pairs/${pairId}/hide-from-opener`
      );
      return data;
    },
    {
      onMutate: async (pairId) => {
        // The pair list is cached PER SCOPE (`pairsQueryKey`), so the operator
        // may hold several at once — the open group's and, if they visited it,
        // the bucket page's unscoped one. Patch every visible-pair list rather
        // than one hard-coded key, or the row survives the hide in whichever
        // list wasn't named. The `hidden` list is deliberately excluded: the
        // pair BELONGS there after a hide, so removing it would be backwards.
        await qc.cancelQueries({ predicate: isVisiblePairsQuery });
        const prev = qc.getQueriesData<ManualConfirmedPair[]>({
          predicate: isVisiblePairsQuery
        });
        // Patch only lists that actually hold data. `setQueriesData` with an
        // updater would also fire for a query that has never resolved, seeding
        // it with `[]` and making an unfetched scope look genuinely empty.
        prev.forEach(([key, list]) => {
          if (Array.isArray(list)) {
            qc.setQueryData<ManualConfirmedPair[]>(
              key,
              list.filter((p) => p._id !== pairId)
            );
          }
        });
        return { prev };
      },
      onError: (_err, _pairId, ctx) => {
        // Rollback the optimistic removal on failure so the row
        // pops back rather than silently disappearing forever. Guarded
        // symmetrically with the forward patch: the snapshot holds an entry for
        // EVERY matching query, including ones still in flight whose data is
        // undefined, and `setQueryData(key, undefined)` promotes such a query
        // to `status: 'success'` with no data — which renders as a genuinely
        // empty table until the next fetch lands.
        ctx?.prev?.forEach(([key, list]) => {
          if (Array.isArray(list)) qc.setQueryData(key, list);
        });
      },
      onSettled: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/**
 * Per-pair opener kill switch — flips `prediction_pairs.openerEnabled`.
 * Served by the opener-orchestrator: this is the market-data switch the
 * operator needs to arm a rule, so it cannot depend on the
 * opportunities-orchestrator, which scales to 0 independently of the opener.
 * Newly manual-confirmed pairs default to false; toggling to true is what
 * makes the WS subs start streaming and lets `RulesService.activate`
 * succeed. Toggling to false on the backend auto-pauses every active rule
 * on the pair and lets the pruner drop the WS books (~5 min). The Entry
 * page reads the live state from the `opening_state_update` envelope
 * (field `pairEnabled`), so this mutation does NOT optimistically patch
 * a separate cache — the WS bridge is the source of truth.
 */
export function useSetPairOpenerEnabled() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, enabled }: { pairId: string; enabled: boolean }) => {
      const { data } = await openerOrchestratorAxiosApi.patch(
        `/pairs/${pairId}/opener-enabled`,
        { enabled }
      );
      return data;
    },
    {
      mutationKey: 'entry:set-pair-opener-enabled',
      // Refresh the pair list so `pair.openerEnabled` reflects the new value —
      // the maker-flag arm path reads it to decide whether to auto-enable the
      // pair first (see `needsOpenerAutoEnable`), so a stale `false` would make
      // it re-enable redundantly until the 30s poll.
      onSettled: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/**
 * Bulk per-pair opener kill switch — flips `prediction_pairs.openerEnabled`
 * on many pairs in one round trip. `scope: 'ids'` flips the given pairIds;
 * `scope: 'all'` flips every opener-eligible pair server-side (so the UI
 * never has to ship hundreds of ids). Served by the opener-orchestrator, for
 * the same reason as the single-pair switch. Same cascade as the single-pair
 * `useSetPairOpenerEnabled` (auto-pause rules + WS prune within ~5 min);
 * live state still arrives via the `opening_state_update` envelope, so this
 * only invalidates the pair list to refresh row metadata.
 */
export function useBulkSetPairOpenerEnabled() {
  const qc = useQueryClient();
  return useMutation(
    async (vars: {
      pairIds?: string[];
      scope?: 'ids' | 'all';
      enabled: boolean;
      groupId?: string;
    }) => {
      const { data } = await openerOrchestratorAxiosApi.patch(
        '/pairs/bulk-opener-enabled',
        vars
      );
      return data as {
        scope: 'ids' | 'all';
        matched: number;
        modified: number;
      };
    },
    {
      mutationKey: 'entry:bulk-opener-enabled',
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(`/rules/${id}/activate`)
      );
      return data;
    },
    {
      // Tagged so the app-wide MutationCache.onError backstop surfaces a 409
      // (e.g. core refusing a grouped-pair activation) instead of failing
      // silently — the groups-only stem routing in mutationErrorHandler still
      // applies regardless of this key.
      mutationKey: 'entry:activate',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(`/rules/${id}/pause`)
      );
      return data;
    },
    {
      mutationKey: 'entry:pause',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Explicit arm/disarm intent (spec D5/O6). The operator's on/off gesture sends
 * a single explicit boolean — `PATCH /rules/{id}` with `{ desiredArmed }` — and
 * that boolean is the ONLY thing that arms or pauses the rule. The backend no
 * longer INFERS arming from a maker-flag `false→true` transition or from a
 * params `update`, so a stale/other-writer update can never silently re-arm a
 * rule the operator turned off. The maker flags stay pure per-leg config.
 *
 * Routed by `resolveOpenerApiForRule` (grouped → owning feed pod, fail-visible
 * if unresolvable; ungrouped → core), enqueued on the shared opener-write
 * budget, and tagged `entry:set-armed` so the app-wide `MutationCache.onError`
 * backstop surfaces a failure with tailored copy. PATCH (not a dedicated
 * endpoint) keeps the write on the same routed instance as every other per-rule
 * mutation.
 */
export function useSetRuleArmed() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, desiredArmed }: { id: string; desiredArmed: boolean }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).patch<EntryRule>(`/rules/${id}`, {
          desiredArmed
        })
      );
      return data;
    },
    {
      mutationKey: 'entry:set-armed',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Toggle a rule's `allowThresholdAboveOne` flag — the per-rule opt-in that
 * lets an `always-threshold` rule's threshold rest above $1 (up to 1.4).
 * Mirrors `useActivateRule`: group-scoped client resolution, a `mutationKey`
 * so the app-wide `MutationCache.onError` backstop surfaces the 400 the
 * backend returns when disabling while the threshold is still ≥ 1, and an
 * `entry-rules` invalidate on success.
 */
export function useSetAllowThresholdAboveOne() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(
          `/rules/${id}/allow-threshold-above-one`,
          { enabled }
        )
      );
      return data;
    },
    {
      mutationKey: 'entry:allow-threshold-above-one',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useEntryRuleExecutions(ruleId: string | null) {
  return useQuery<EntryExecution[]>(
    ['entry-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionOpenerAxiosApi.get<EntryExecution[]>(
        `/rules/${ruleId}/executions`
      );
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

export function useEntryOrders() {
  return useQuery<EntryOrdersResponse>(
    'entry-orders',
    async () => {
      const { data } = await positionOpenerAxiosApi.get<EntryOrdersResponse>(
        '/orders'
      );
      return data;
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

/**
 * Pair-manager response shape: each pair has `siteA` / `siteB` legs,
 * each tagged with a `platform`. siteA is *usually* Polymarket but the
 * adapter below treats platform as the source of truth so a future
 * matcher flip (Kalshi-first) doesn't silently misalign the legs.
 */
interface PairManagerSite {
  platform: 'polymarket' | 'kalshi';
  marketId?: string;
  title?: string;
  marketUrl?: string;
  yesTokenId?: string;
  noTokenId?: string;
  canonicalKey?: string;
  // Event/market start time (ISO) — Polymarket carries the real kickoff
  // (gameStartTime/startDate); Kalshi's can be the trading-open time. Present
  // per site on the pair-manager doc and already returned by the read.
  startDate?: string | null;
  // Kalshi event ticker — shared across sibling markets of one event, so it
  // groups a match's markets when populated.
  eventTicker?: string | null;
}
export interface PairManagerPair {
  _id: string;
  name?: string;
  pairName?: string;
  status?: string;
  archived?: boolean;
  expiryDate?: string | null;
  category?: string | null;
  subcategory?: string | null;
  marketType?: string | null;
  openerGroupId?: string | null;
  openerSubgroupId?: string | null;
  openerEnabled?: boolean;
  settledOnKalshi?: boolean;
  settledAt?: string | null;
  kalshiResult?: 'yes' | 'no' | null;
  siteA?: PairManagerSite;
  siteB?: PairManagerSite;
}

/** Parse any date-ish value to a normalized ISO string, or null. */
function toIsoStart(v?: string | null): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Polymarket splits ONE match across several event slugs — the main event page
// plus descriptive sub-event pages that append a suffix (e.g. a Brasileirão
// match: `bra2-crb-vln-2026-07-27`, `…-more-markets`, `…-halftime-result`).
// Stripping the suffix collapses all of a match's markets to one grouping key.
// Add new suffixes here if a league introduces another sub-event page.
const POLY_EVENT_SLUG_SUFFIXES = ['-more-markets', '-halftime-result'];

/**
 * Polymarket event slug from a market URL (`/event/<slug>[/<market>]`), with any
 * known sub-event suffix stripped so every market of one match shares the slug.
 */
// The per-match base is `<prefix>-<teams>-<YYYY-MM-DD>`. Keeping everything up
// to the date and dropping whatever follows collapses ALL of a match's sub-event
// pages (-more-markets, -halftime-result, -exact-score, -first-to-score, and any
// future one — Kalshi/Poly add these per market type) to one grouping key.
const POLY_SLUG_DATE_BASE = /^.*-\d{4}-\d{2}-\d{2}/;

function polyEventSlug(url?: string | null): string | null {
  if (!url) return null;
  const m = url.match(/\/event\/([^/?#]+)/i);
  if (!m) return null;
  const slug = m[1].toLowerCase();
  // Dated sports slug → keep up to the date, drop any sub-event suffix.
  const dated = slug.match(POLY_SLUG_DATE_BASE);
  if (dated) return dated[0];
  // Non-dated slug (non-sports event) → strip a known suffix if present.
  for (const suffix of POLY_EVENT_SLUG_SUFFIXES) {
    if (slug.endsWith(suffix)) return slug.slice(0, -suffix.length);
  }
  return slug;
}

/**
 * Resolve a pair's event start time (ISO) — the real kickoff for a
 * match-related row. Prefers the Polymarket side's `startDate`
 * (gameStartTime-derived, reliable) over Kalshi's (which can be the
 * trading-open time). Returns null when neither side has a usable start —
 * that null is the gate for "not a match-related row" in the UI.
 * Exported for unit tests.
 */
export function resolveEventStartTime(raw: PairManagerPair): string | null {
  const sites = [raw.siteA, raw.siteB].filter(
    (s): s is PairManagerSite => !!s,
  );
  const poly = sites.find(s => s.platform === 'polymarket');
  const kalshi = sites.find(s => s.platform === 'kalshi');
  return toIsoStart(poly?.startDate) ?? toIsoStart(kalshi?.startDate);
}

/**
 * Resolve a stable per-match grouping key — identical for every market of one
 * match. Prefers the Polymarket event slug (reduced to its per-match base):
 * Polymarket groups a whole match under ONE event (+ sub-pages), so its slug is
 * per-MATCH. The Kalshi event ticker is only a fallback because it is per-EVENT
 * = per-market-type SERIES for the same match (KXUCLGAME / KXUCLSPREAD /
 * KXUCL1H … all for one game), so grouping by it would SPLIT a match by market
 * type. Then the Kalshi market URL, then the pair id (its own singleton bucket —
 * never groups unrelated rows). Exported for unit tests.
 */
export function resolveEventKey(raw: PairManagerPair): string | null {
  const sites = [raw.siteA, raw.siteB].filter(
    (s): s is PairManagerSite => !!s,
  );
  const poly = sites.find(s => s.platform === 'polymarket');
  const kalshi = sites.find(s => s.platform === 'kalshi');
  const slug = polyEventSlug(poly?.marketUrl);
  if (slug) return `p:${slug}`;
  const ticker = (kalshi?.eventTicker ?? poly?.eventTicker)?.trim();
  if (ticker) return `k:${ticker.toUpperCase()}`;
  if (kalshi?.marketUrl) return `u:${kalshi.marketUrl}`;
  return raw._id ? `id:${raw._id}` : null;
}

export function adaptPair(raw: PairManagerPair): ManualConfirmedPair {
  const sites = [raw.siteA, raw.siteB].filter(
    (s): s is PairManagerSite => !!s,
  );
  const polySite = sites.find(s => s.platform === 'polymarket');
  const kalshiSite = sites.find(s => s.platform === 'kalshi');
  return {
    _id: raw._id,
    pairName: raw.pairName ?? raw.name ?? '',
    polymarketTitle: polySite?.title,
    kalshiTitle: kalshiSite?.title,
    expiryDate: raw.expiryDate ?? null,
    category: raw.category ?? null,
    subcategory: raw.subcategory ?? null,
    marketType: raw.marketType ?? null,
    openerGroupId: raw.openerGroupId ?? null,
    openerSubgroupId: raw.openerSubgroupId ?? null,
    openerEnabled: raw.openerEnabled ?? false,
    status: raw.status,
    archived: raw.archived,
    settledOnKalshi: raw.settledOnKalshi ?? false,
    settledAt: raw.settledAt ?? null,
    kalshiResult: raw.kalshiResult ?? null,
    eventStartTime: resolveEventStartTime(raw),
    eventKey: resolveEventKey(raw),
    polymarket: polySite
      ? {
          yesTokenId: polySite.yesTokenId,
          noTokenId: polySite.noTokenId,
          conditionId: polySite.marketId,
          marketUrl: polySite.marketUrl,
        }
      : null,
    kalshi: kalshiSite
      ? {
          ticker: kalshiSite.marketId,
          marketUrl: kalshiSite.marketUrl,
        }
      : null,
  };
}

/**
 * PANIC button — operator-initiated emergency stop on the opener:
 * batch-cancel every active maker buy and pause every entry rule. The
 * resulting cancellation events are surfaced as desired operator
 * cancellations (severity = warning, not error) so the in-app feed +
 * Slack distinguish them from engine faults.
 *
 * Resilience: fans out to BOTH the position-opener /rules/panic AND
 * prediction-manager /api/system/panic (which pauses every active entry
 * rule directly in Mongo + best-effort kicks the service again).
 * `Promise.allSettled` so one unreachable pod can never leave the system
 * armed — at least one of the two paths is enough to guarantee no new
 * orders. Duplicate panic on the service is idempotent.
 */
export function useEntryPanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionOpenerAxiosApi.post('/rules/panic'),
        openerOrchestratorAxiosApi.post('/api/system/panic', { target: 'opener' }),
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`,
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null,
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

/**
 * Soft panic (24h) for the opener — same resilient fan-out as
 * `useEntryPanic`, but cancels buys only on markets of entry rules active
 * in the last 24h. Orders on unrelated markets are left untouched.
 */
export function useEntryPanicRecent() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionOpenerAxiosApi.post('/rules/panic-recent'),
        openerOrchestratorAxiosApi.post('/api/system/panic-recent', { target: 'opener' }),
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both soft-panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`,
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null,
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

/**
 * Stop/Start the position-opener deployment via prediction-manager. Mirrors
 * the unwinder controls: stop runs a precondition check + (optional) panic
 * + scale-to-0; start scales to 1 and waits for Ready. The state hook polls
 * every 3 s so the button flips promptly once the patch lands.
 */
export function useOpenerState() {
  return useQuery<OpenerState>(['opener-state'], getOpenerState, {
    refetchInterval: 3_000,
    keepPreviousData: true,
  });
}

export function useStopOpener() {
  const qc = useQueryClient();
  return useMutation(stopOpener, {
    onSuccess: job => {
      // Seed system/job so UpdatesProvider's stepper picks it up immediately.
      // EntryPage wraps the route in `<UpdatesProvider scope="predictions">`,
      // and that provider's job-poll query key is `['system/job', scope]` —
      // a string-only key like 'system/job' silently misses, leaving the
      // stepper popover invisible and the user with no feedback.
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries('entry-rules');
      qc.invalidateQueries(['opener-state']);
    },
  });
}

/**
 * START is ONE button for the operator.
 *
 * `/api/system/opener/start` is served by the opener-orchestrator pod, which
 * defaults to 0 replicas — so starting used to be two presses: "Boot control
 * plane", wait for it, then "Start Opener". Which pod serves which route is
 * plumbing, not a decision the operator should be making, so START boots the
 * control plane itself when it is down and only then starts.
 *
 * The probe inside `ensureOrchestratorBooted` keeps the already-running case at
 * one extra GET.
 */
export function useStartOpener() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      await ensureOrchestratorBooted('opener');
      return startOpener();
    },
    {
      mutationKey: 'entry:start-opener',
      onSuccess: job => {
        qc.setQueryData(['system/job', 'predictions'], job);
        qc.invalidateQueries(['system/job', 'predictions']);
        qc.invalidateQueries(['opener-state']);
      },
    }
  );
}

export function useClearOpenerPanicLockout() {
  const qc = useQueryClient();
  return useMutation(clearOpenerPanicLockout, {
    onSuccess: () => {
      // Re-fetch state so the lockout banner clears + START re-enables.
      qc.invalidateQueries(['opener-state']);
    },
  });
}

/**
 * Boot the opener-orchestrator control plane (P5, mirror of
 * `useBootUnwinderOrchestrator`). The orchestrator pod is root-booted by an
 * explicit operator action and it, in turn, serves the `/api/system/opener/*`
 * on/off routes plus the opener rule-CRUD node that Start Opener and the entry
 * table depend on. The knob lives on the MANAGER base, NOT on the orchestrator
 * itself — the orchestrator can't boot itself.
 */
export function useBootOpenerOrchestrator() {
  return useBootOrchestrator('opener');
}

/**
 * Shut down the opener-orchestrator control plane. Mirror of
 * `useShutdownUnwinderOrchestrator` — also on the MANAGER base.
 */
export function useShutdownOpenerOrchestrator() {
  return useShutdownOrchestrator('opener');
}

/**
 * Query key for a pair list. The scope segment is what stops the group-scoped
 * opener read and the unscoped bucket-page read from overwriting each other in
 * the cache — without it, opening a group would poison the bucket page's list
 * with 66 pairs and vice versa.
 */
export function pairsQueryKey(selection: GroupSelection = ALL_GROUPS) {
  return [
    'manual-confirmed-pairs',
    OPENER_PAIR_STATUS,
    groupSelectionKey(selection)
  ];
}

/**
 * Matches every VISIBLE-pair list, at any scope — the keys `pairsQueryKey`
 * builds. Excludes the hidden bucket (`['manual-confirmed-pairs', 'hidden']`),
 * which is keyed by a different second segment. Exported for tests.
 */
export function isVisiblePairsQuery(query: { queryKey: unknown }): boolean {
  const key = query.queryKey;
  return (
    Array.isArray(key) &&
    key[0] === 'manual-confirmed-pairs' &&
    key[1] === OPENER_PAIR_STATUS
  );
}

/**
 * Manually-confirmed prediction pairs that are eligible for entry rules.
 *
 * Served by the OPENER (`GET /pairs` on position-opener), scoped to the group
 * being viewed — the same routing `useEntryRules` uses, via `resolveOpenerApi`.
 * This read used to hit opportunities-orchestrator for the whole ~3.6k-pair
 * universe (~10 MB) on every view, and the opener's rules table blocks on it,
 * so a degraded link put the operator on a spinner for a minute and a half
 * while pairs they would never render arrived. A group now fetches only its
 * own pairs, from the pod that owns them.
 *
 * Omitting `groupSelection` keeps the UNSCOPED universe on the core opener pod
 * — what the Pairs Bucket page needs in order to assign pairs across groups.
 *
 * The backend returns the raw matcher schema (`siteA`/`siteB`/`name`);
 * `adaptPair` flattens that into the frontend's `ManualConfirmedPair`
 * interface so the row component doesn't need to know about the storage
 * shape. Refetches every 30 s so newly confirmed pairs show up without a
 * hard reload.
 */
export function useManualConfirmedPairs(groupSelection?: GroupSelection) {
  const selection = groupSelection ?? ALL_GROUPS;
  // Mirrors `useEntryRules`: a concrete group → its own id (→ that group's feed
  // pod); `'ungrouped'` → `''` (core, null bucket only); `'all'` → `null`, the
  // unscoped universe on core. `null` distinguishes "don't send a param" from
  // "send the empty-string param".
  const groupId =
    selection.kind === 'group'
      ? selection.groupId
      : selection.kind === 'ungrouped'
        ? ''
        : null;
  return useQuery<ManualConfirmedPair[]>(
    pairsQueryKey(selection),
    async () => {
      const params: Record<string, string> = { status: OPENER_PAIR_STATUS };
      if (groupId !== null) params.groupId = groupId;
      const { data } = await resolveOpenerApi(groupId).get<PairManagerPair[]>(
        '/pairs',
        { params }
      );
      return Array.isArray(data) ? data.map(adaptPair) : [];
    },
    {
      // Mirrors `useEntryRules`' guard, for the same reason: under `'all'` the
      // Entry page renders the group picker and never reads these pairs, so
      // firing the UNSCOPED query there would poll the whole catalog off the
      // core opener pod every 30 s for a screen showing none of it.
      //
      // Keyed on whether a selection was SUPPLIED, not on the resolved value:
      // the Pairs Bucket page calls this with no argument and genuinely needs
      // the unscoped list, and it defaults to the same `ALL_GROUPS`.
      enabled: groupSelection === undefined || selection.kind !== 'all',
      refetchInterval: 30_000,
      keepPreviousData: true
    }
  );
}
